<template>
  <div class="rise-in card card-pad">
    <div class="max-w-4xl mx-auto">
      <div class="mb-6">
        <router-link
          class="link-brand inline-flex items-center gap-1.5 text-sm"
          :to="{ name: 'PatientOverview', params: { projectSlug, patientId } }"
        >
          <font-awesome-icon icon="arrow-left" /> {{ patientId }}
        </router-link>
        <h1 class="page-title mt-2">
          {{ form ? form.title : title }}
        </h1>
        <p v-if="form && form.subtitle" class="page-subtitle">
          {{ form.subtitle }}
        </p>
      </div>

      <!-- Carried-over notice (follow-up visits). Cleared on first save. -->
      <div
        v-if="showCopiedBanner"
        class="flex items-start gap-3 mb-6 p-4 rounded-xl bg-brand-50 border border-brand-100"
      >
        <font-awesome-icon
          icon="info-circle"
          class="text-brand-600 mt-0.5 flex-shrink-0"
        />
        <p class="text-sm text-ink-soft leading-relaxed">
          <span class="font-semibold text-ink"
            >Pre-filled from visit {{ previousVisitNumber }}.</span
          >
          {{ copiedSectionNames.join(', ') }} carried over from the previous
          visit — please review and save to confirm.
        </p>
      </div>

      <!-- Chapter picker (visit forms) -->
      <nav
        v-if="chaptersList.length"
        class="flex flex-wrap gap-2 mb-8 border-b border-gray-100 pb-5"
      >
        <router-link
          v-for="(c, i) in chaptersList"
          :key="c.key"
          class="inline-flex items-center text-xs font-semibold px-3.5 py-1.5 rounded-full transition-colors"
          :class="
            String(i) === String(activeIndex)
              ? 'bg-brand-600 text-white shadow-sm'
              : 'bg-brand-50 text-brand-700 hover:bg-brand-100'
          "
          :to="{
            name: 'Visit',
            params: { projectSlug, patientId, visitNumber, chapter: String(i) },
          }"
        >
          {{ c.name }}
          <span
            v-if="copiedChapterKeys.has(c.key)"
            class="ml-1.5 w-1.5 h-1.5 rounded-full"
            :class="
              String(i) === String(activeIndex) ? 'bg-white' : 'bg-brand-400'
            "
            title="Carried over from previous visit"
          ></span>
        </router-link>
      </nav>

      <div
        v-if="loadingForm"
        class="flex items-center gap-2 text-ink-muted py-8"
      >
        <font-awesome-icon icon="circle-notch" spin class="text-brand-600" />
        Loading…
      </div>

      <template v-else>
        <h2 v-if="chapterName" class="text-lg font-bold text-ink mb-4">
          {{ chapterName }}
        </h2>

        <template v-if="schema">
          <!-- keep-model-data: don't prune a chapter's answers from `values`
               when its inputs unmount on chapter switch (they're only persisted
               on save; without this, navigating tabs drops unsaved entries). -->
          <FormulateForm
            v-model="values"
            :schema="schema"
            :keep-model-data="true"
          />
          <div class="flex justify-end mt-8">
            <button
              class="btn btn-primary btn-lg uppercase tracking-wide"
              :disabled="saving"
              @click="save(values)"
            >
              <font-awesome-icon v-if="saving" icon="circle-notch" spin />
              {{ saveButtonLabel }}
            </button>
          </div>
        </template>

        <!-- Fallback while a schema is still being reconstructed -->
        <div
          v-else
          class="rounded-xl border border-dashed border-gray-300 p-4 text-sm text-ink-soft"
        >
          Schema for <code>{{ schemaName }}</code> not reconstructed yet.
          Existing answers are preserved and can be saved as-is.
          <pre class="mt-2 overflow-auto text-xs bg-gray-50 p-2 rounded">{{
            values
          }}</pre>
          <button class="btn btn-primary btn-md mt-3" @click="save(values)">
            Save
          </button>
        </div>

        <p
          v-if="error"
          class="text-red-600 text-sm mt-3 flex items-center gap-2"
        >
          {{ error }}
        </p>
        <p
          v-if="saved && !requiredWarning.length"
          class="text-green-600 text-sm font-semibold mt-3 flex items-center gap-2"
        >
          <font-awesome-icon icon="check-circle" /> Saved.
        </p>
        <div
          v-if="saved && requiredWarning.length"
          class="flex items-start gap-3 mt-4 p-4 rounded-xl bg-yellow-50 border border-yellow-200"
        >
          <font-awesome-icon
            icon="exclamation-triangle"
            class="text-yellow-600 mt-0.5 flex-shrink-0"
          />
          <p class="text-sm text-ink-soft">
            <span class="font-semibold text-ink"
              >Saved — but some required fields are still empty:</span
            >
            {{ requiredWarning.map(r => r.label).join(', ') }}. Please complete
            them when possible.
          </p>
        </div>
      </template>
      <!-- Confirm changes modal -->
      <ConfirmChangesModal
        :is-open="showConfirmModal"
        :changes="activeChanges"
        @confirm="submitCorrection"
        @cancel="showConfirmModal = false"
      />
    </div>
  </div>
</template>

<script>
import { rest } from 'helpers/api'
import { toFormValues, toStoreValues } from 'helpers/groupShape'
import ConfirmChangesModal from 'components/ConfirmChangesModal.vue'

// Fields the backend carries over, grouped by the boolean flag it stamps.
const CARRIED_BY_FLAG = {
  copiedAnthropometrics: ['dateOfBirth', 'sex'],
  copiedSmoking: ['smoking'],
  copiedMedication: ['medication'],
  copiedMorbidities: ['morbidities', 'comments'],
  copiedComorbidities: ['comorbidities'],
}

// Soft-required fields for the "Patient Data Collection" (visit) form. Missing
// ones are warned about and flagged on save, but the save is never blocked.
// Only fields that actually exist in the form's schema are checked.
const REQUIRED_FIELDS = [
  { name: 'dateOfVisit', label: 'Date of Visit' },
  { name: 'dateOfBirth', label: 'Date of Birth' },
  { name: 'sex', label: 'Sex' },
  { name: 'weight', label: 'Weight (kg)' },
  { name: 'height', label: 'Height (cm)' },
]

export default {
  name: 'ClinicalForm',
  components: {
    ConfirmChangesModal,
  },
  props: {
    // 'screening' | 'visit' | 'visit-data'
    formKeyPrefix: { type: String, required: true },
    title: { type: String, default: '' },
    // numeric chapter index (visit forms) as a string, e.g. '0'
    chapter: { type: String, default: null },
    mode: { type: String, default: 'entry' },
  },
  data() {
    return {
      form: null,
      values: {},
      originalValues: {},
      def: null,
      schema: null,
      chaptersList: [], // [{ key, name, schema }] when the form has chapters
      chapterName: null,
      loadingForm: true,
      error: null,
      saved: false,
      saving: false,
      showConfirmModal: false,
      activeChanges: [],
      requiredWarning: [], // [{name,label}] required fields still empty after a save
      showRequiredErrors: false,
      operators: null, // live operator list for the "Operator in charge" picker
    }
  },
  computed: {
    projectSlug() {
      return this.$route.params.projectSlug
    },
    patientId() {
      return this.$route.params.patientId
    },
    visitNumber() {
      return this.$route.params.visitNumber || '1'
    },
    activeIndex() {
      return this.chapter == null ? 0 : Number(this.chapter)
    },
    // True when this chaptered form has a chapter after the active one.
    hasNextChapter() {
      return (
        this.chaptersList.length > 0 &&
        this.activeIndex < this.chaptersList.length - 1
      )
    },
    saveButtonLabel() {
      if (this.saving) return 'Saving…'
      return this.hasNextChapter ? 'Save & continue' : 'Save'
    },
    isFollowUp() {
      return Number(this.visitNumber) > 1
    },
    previousVisitNumber() {
      return Number(this.visitNumber) - 1
    },
    // Field names carried over from the previous visit (for field-level marking).
    carriedFieldNames() {
      const v = this.values || {}
      const s = new Set()
      if (this.isFollowUp) {
        Object.keys(CARRIED_BY_FLAG).forEach(flag => {
          if (v[flag]) CARRIED_BY_FLAG[flag].forEach(n => s.add(n))
        })
      }
      return s
    },
    // Every field name present anywhere in this form's definition.
    allFieldNames() {
      const names = new Set()
      const walk = fields => {
        if (!Array.isArray(fields)) return
        fields.forEach(f => {
          if (f && f.name) names.add(f.name)
          if (f && f.children) walk(f.children)
        })
      }
      const def = this.def
      if (def) {
        if (def.chapters) def.chapters.forEach(c => walk(c.schema))
        else walk(def.schema || def)
      }
      return names
    },
    // All field defs across every chapter (or the single schema), flattened to
    // one list. Group names are unique across chapters, so this drives the
    // load/save group-shape reconciliation over the flat form data.
    formFields() {
      const def = this.def
      if (!def) return []
      if (def.chapters)
        return def.chapters.reduce((acc, c) => acc.concat(c.schema || []), [])
      return def.schema || def
    },
    // Required fields that actually exist in this (visit) form's schema.
    requiredFields() {
      if (this.formKeyPrefix !== 'visit') return []
      return REQUIRED_FIELDS.filter(r => this.allFieldNames.has(r.name))
    },
    // Sections the backend carried over from the previous visit. When a
    // follow-up visit is created (POST /addForms) the backend copies active
    // medication/morbidities/etc. and stamps boolean "copied*" flags into the
    // form data. We surface those as a review banner + chapter-tab markers,
    // then clear the flags on first save (see save()).
    copiedChapterKeys() {
      const v = this.values || {}
      // flag -> chapter key(s) it should mark (comorbidities live in the
      // morbidities chapter, so its flag marks that chapter too).
      const map = {
        copiedAnthropometrics: ['anthropometrics'],
        copiedSmoking: ['smokingHistory'],
        copiedMedication: ['medication'],
        copiedMorbidities: ['morbidities'],
        copiedComorbidities: ['comorbidities', 'morbidities'],
      }
      const keys = new Set()
      if (this.isFollowUp) {
        const present = new Set(this.chaptersList.map(c => c.key))
        Object.keys(map).forEach(flag => {
          if (v[flag])
            map[flag].forEach(k => {
              if (present.has(k)) keys.add(k)
            })
        })
      }
      return keys
    },
    copiedSectionNames() {
      return this.chaptersList
        .filter(c => this.copiedChapterKeys.has(c.key))
        .map(c => c.name)
    },
    showCopiedBanner() {
      return this.isFollowUp && this.copiedSectionNames.length > 0
    },
    // Map prefix + visit -> the canonical forms.key produced by FormsFactory.
    formKey() {
      const n = this.visitNumber
      if (this.formKeyPrefix === 'screening')
        return n === '1' ? 'screening' : `screening${n}`
      if (this.formKeyPrefix === 'visit-data') return `visit${n}-data`
      return `visit${n}`
    },
    schemaName() {
      return this.chapter
        ? `${this.formKeyPrefix}[${this.chapter}]`
        : this.formKeyPrefix
    },
  },
  watch: {
    // Re-resolve the active chapter when navigating the picker. Clear the
    // previous chapter's save/warning state so it doesn't bleed onto the new tab.
    chapter() {
      this.saved = false
      this.error = null
      this.requiredWarning = []
      this.resolveChapter()
    },
    // Re-apply field markers + recompute derived values (pack-years) when the
    // model changes (edits, added repeatable rows). Pack-years is recomputed only
    // once the form is "armed" (after the initial hydrate) so loading a record
    // never rewrites its stored value — see recomputePackYears / created().
    values: {
      deep: true,
      handler() {
        if (this.recalcArmed) this.recomputePackYears(this.values)
        this.markFields()
      },
    },
  },
  async created() {
    // Non-reactive guard: pack-years auto-calc stays off until after the initial
    // hydrate so loading a record can't overwrite its stored pack-years.
    this.recalcArmed = false
    await this.load()
    // Fetch the live operator list in the background; it re-injects the
    // "Operator in charge" options once resolved (schema still renders meanwhile).
    this.loadOperators()
    await this.loadSchema()
    // Hydrate the model into vue-formulate's group shape, THEN render the schema
    // (must happen before <FormulateForm> mounts, or groups won't populate).
    this.hydrate()
    this.resolveChapter()
    // Arm pack-years recompute only after the hydrate-triggered `values` watcher
    // has flushed, so opening a record never rewrites its stored pack-years.
    this.$nextTick(() => {
      this.recalcArmed = true
    })
  },
  methods: {
    async load() {
      this.loadingForm = true
      try {
        this.form = await rest.getForm(this.patientId, this.formKey)
        this.values = (this.form && this.form.data) || {}
        this.originalValues = JSON.parse(JSON.stringify(this.values))
      } catch (e) {
        this.error = e.message
      } finally {
        this.loadingForm = false
      }
    },
    async loadSchema() {
      // Visit forms have a per-project chapter set in schemas/visit/<project>.js (or <project>.v2.js for subsequent visits).
      // Other forms (screening) live at schemas/<prefix>.js as { schema:[...] }.
      try {
        if (this.formKeyPrefix === 'visit') {
          // Per-project chapter set. If a project's schema isn't built yet we
          // intentionally fall through to the raw-data editor rather than show
          // another project's (wrong) fields — important for clinical safety.
          const suffix = Number(this.visitNumber) > 1 ? '.v2' : ''
          const mod = await import(
            `@/forms/schemas/visit/${this.projectSlug}${suffix}.js`
          )
          this.def = mod.default || mod
        } else {
          const mod = await import(`@/forms/schemas/${this.formKeyPrefix}.js`)
          this.def = mod.default || mod
        }
      } catch (e) {
        this.def = null
      }
      // NB: resolveChapter() is called by created() after hydrate(), and by the
      // `chapter` watcher on navigation — not here, so the model is in group
      // shape before the form first renders.
    },
    // Reshape the stored form data into vue-formulate's group model (arrays) so
    // pre-filled values actually populate the inputs. See helpers/groupShape.js.
    hydrate() {
      const raw = (this.form && this.form.data) || {}
      this.values = toFormValues(this.formFields, raw)
      this.armPackYearsModes(this.values)
      this.originalValues = JSON.parse(JSON.stringify(this.values))
    },
    resolveChapter() {
      const def = this.def
      if (!def) {
        this.schema = null
        return
      }
      if (def.chapters) {
        this.chaptersList = def.chapters
        const ch = def.chapters[this.activeIndex]
        this.schema = ch ? ch.schema : null
        this.chapterName = ch ? ch.name : null
      } else {
        this.schema = def.schema || def
      }
      this.injectOperatorOptions()
      this.markFields()
    },
    // Pull the live operator list, then refresh the picker options. On failure
    // the schema keeps its shipped (static) fallback list, so entry still works.
    async loadOperators() {
      try {
        this.operators = await rest.listOperators()
        this.injectOperatorOptions()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load operator list:', e)
      }
    },
    // Replace the "Operator in charge" dropdown's options with the live operator
    // emails. Any value already stored on this record that isn't a current
    // operator (legacy short-name keys, since-removed staff) is kept as an option
    // so old entries still display and aren't silently blanked on save.
    injectOperatorOptions() {
      if (!this.operators || !Array.isArray(this.schema)) return
      const options = { '': '' }
      this.operators.forEach(o => {
        options[o.email] = o.email
      })
      // After hydrate(), breathAnalysis is vue-formulate's array-of-one group.
      const ba = this.values && this.values.breathAnalysis
      const baObj = Array.isArray(ba) ? ba[0] : ba
      const current = (baObj && baObj.operatorInCharge) || ''
      if (current && !(current in options)) options[current] = current
      let found = false
      const walk = fields => {
        if (!Array.isArray(fields)) return
        fields.forEach(f => {
          if (f && f.name === 'operatorInCharge') {
            f.options = options
            found = true
          }
          if (f && f.children) walk(f.children)
        })
      }
      walk(this.schema)
      // Re-assign a shallow copy so FormulateForm picks up the new options
      // (nested field object refs — incl. any fn props — are preserved).
      if (found) this.schema = [...this.schema]
    },
    // Derive pack-years for any tobacco entry from cigarettes/day and the smoking
    // span: pack-years = (cigarettes/day ÷ 20) × (year ended − year started).
    // A missing year-ended means the patient still smokes → use the current year.
    // Only runs while `recalcArmed` (i.e. after the operator edits, never on load,
    // so stored historical pack-years are preserved). Walks the model so it works
    // whatever the group nesting; guarded (only writes on change) so it doesn't
    // loop the deep `values` watcher.
    recomputePackYears(node) {
      if (Array.isArray(node)) {
        node.forEach(n => this.recomputePackYears(n))
        return
      }
      if (!node || typeof node !== 'object') return
      if (
        'cigsPerDay' in node &&
        'yearStarted' in node &&
        'packYears' in node &&
        // Manual rows keep the operator's entered pack-years verbatim.
        node.packYearsMode !== 'manual'
      ) {
        const start = parseFloat(node.yearStarted)
        // Only compute once a valid start year is present; otherwise leave the
        // stored pack-years untouched.
        if (isFinite(start)) {
          const cigs = parseFloat(node.cigsPerDay) || 0
          const endRaw = parseFloat(node.yearEnded)
          const end = isFinite(endRaw) ? endRaw : new Date().getFullYear()
          const yrs = Math.max(0, end - start)
          const py = String(Math.round((cigs / 20) * yrs * 10) / 10)
          if (String(node.packYears) !== py) this.$set(node, 'packYears', py)
        }
      }
      Object.keys(node).forEach(k => {
        const v = node[k]
        if (v && typeof v === 'object') this.recomputePackYears(v)
      })
    },
    // Infer each tobacco row's UI-only auto/manual pack-years mode from the stored
    // value: if the stored pack-years already equals the formula it's "auto";
    // otherwise the value was hand-adjusted (the ~1,886 historical rows that don't
    // match) so it's "manual" and must be preserved. Never persisted — stripped
    // again on save (see stripPackYearsMode). Runs during hydrate, before the
    // recompute is armed, so it can't rewrite stored values.
    armPackYearsModes(node) {
      if (Array.isArray(node)) {
        node.forEach(n => this.armPackYearsModes(n))
        return
      }
      if (!node || typeof node !== 'object') return
      if (
        'cigsPerDay' in node &&
        'yearStarted' in node &&
        'packYears' in node
      ) {
        const start = parseFloat(node.yearStarted)
        const storedStr = node.packYears == null ? '' : String(node.packYears)
        let mode
        if (isFinite(start)) {
          const cigs = parseFloat(node.cigsPerDay) || 0
          const endRaw = parseFloat(node.yearEnded)
          const end = isFinite(endRaw) ? endRaw : new Date().getFullYear()
          const yrs = Math.max(0, end - start)
          const expected = String(Math.round((cigs / 20) * yrs * 10) / 10)
          mode = storedStr === expected ? 'auto' : 'manual'
        } else {
          // No usable start year ⇒ the formula can't reproduce the value, so keep it
          // manual. (Manual is also the default for fresh/empty rows — see bex.js.)
          mode = 'manual'
        }
        this.$set(node, 'packYearsMode', mode)
      }
      Object.keys(node).forEach(k => {
        const v = node[k]
        if (v && typeof v === 'object') this.armPackYearsModes(v)
      })
    },
    // Remove the UI-only packYearsMode flag from a stored-shape payload so it never
    // reaches the database (approach: infer on load, strip on save).
    stripPackYearsMode(node) {
      if (Array.isArray(node)) {
        node.forEach(n => this.stripPackYearsMode(n))
        return
      }
      if (!node || typeof node !== 'object') return
      delete node.packYearsMode
      Object.keys(node).forEach(k => {
        const v = node[k]
        if (v && typeof v === 'object') this.stripPackYearsMode(v)
      })
    },
    // Which required fields are still empty in the given data.
    missingRequired(data) {
      const d = data || this.values || {}
      const empty = v =>
        v === undefined ||
        v === null ||
        (typeof v === 'string' && v.trim() === '')
      return this.requiredFields.filter(r => empty(d[r.name]))
    },
    // Tag each field's wrapper with carried-over / required / required-missing
    // classes (styled globally in _dbi_formulate.scss). Uses each FormulateInput's
    // component instance to read its field name — robust across vue-formulate's
    // group internals.
    markFields() {
      this.$nextTick(() => {
        const rootEl = this.$el
        if (!rootEl || !rootEl.querySelectorAll) return
        const carried = this.carriedFieldNames
        const reqNames = new Set(this.requiredFields.map(r => r.name))
        const missing = this.showRequiredErrors
          ? new Set(this.missingRequired(this.values).map(r => r.name))
          : new Set()
        rootEl.querySelectorAll('.formulate-input').forEach(el => {
          const ctx = el.__vue__ && el.__vue__.context
          const name = ctx && ctx.name
          if (!name) return
          el.classList.toggle('carried-field', carried.has(name))
          el.classList.toggle('is-required', reqNames.has(name))
          el.classList.toggle('required-missing', missing.has(name))
          // Grey the pack-years input while its row's toggle is on "Auto-calculate"
          // (value derived, not typed). The toggle radio sits in the same group row.
          if (name === 'packYears') {
            const row =
              el.closest('.formulate-input-group-item') ||
              el.closest('.formulate-input-group')
            const auto = !!(
              row &&
              row.querySelector('input[type="radio"][value="auto"]:checked')
            )
            el.classList.toggle('pack-years-auto', auto)
          }
        })
        // Cap the year segment of native date/time pickers to 4 digits. The HTML
        // spec allows 6-digit years, so Chromium (Edge/Chrome) reserves 6 slots
        // and won't auto-advance to the next segment until the year is padded
        // with leading zeros. A 4-digit `max` makes it tab forward after 4
        // digits, matching Firefox. `:not([max])` leaves any explicit max alone.
        rootEl
          .querySelectorAll('input[type="datetime-local"]:not([max])')
          .forEach(el => el.setAttribute('max', '9999-12-31T23:59'))
        rootEl
          .querySelectorAll('input[type="date"]:not([max])')
          .forEach(el => el.setAttribute('max', '9999-12-31'))
      })
    },
    async save(values) {
      this.error = null
      this.saved = false
      this.saving = true

      const currentMode = this.$route.query.mode || this.mode
      if (currentMode === 'correct') {
        // Diff in the stored (object/array) shape so the audit trail records the
        // real data change, not vue-formulate's array-wrapped group model.
        const oracle = (this.form && this.form.data) || {}
        const before = toStoreValues(
          this.formFields,
          this.originalValues,
          oracle
        )
        const after = toStoreValues(
          this.formFields,
          values || this.values,
          oracle
        )
        // packYearsMode is UI-only — never diff or record it as a change.
        this.stripPackYearsMode(before)
        this.stripPackYearsMode(after)
        const changes = this.getChanges(before, after)
        if (changes.length === 0) {
          this.error = 'No changes detected.'
          this.saving = false
          return
        }
        this.activeChanges = changes
        this.showConfirmModal = true
        this.saving = false
        return
      }

      try {
        // Merge into existing data so app-stamped metadata (patientId,
        // projectSlug, …) and other chapters' answers are preserved.
        const existing = (this.form && this.form.data) || {}
        // Restore each group's stored shape (object vs array) from the array
        // model vue-formulate maintains, using the pre-edit data as the oracle.
        const restored = toStoreValues(
          this.formFields,
          values || this.values,
          existing
        )
        const data = {
          ...existing,
          ...restored,
          patientId: this.patientId,
          projectSlug: this.projectSlug,
          updatedAt: new Date().toISOString(),
        }
        // Option (b): saving means the clinician has reviewed the values that
        // were carried over from the previous visit, so drop the backend's
        // "copied*" review flags. The copied values themselves stay.
        const COPIED_FLAGS = [
          'copiedAnthropometrics',
          'copiedSmoking',
          'copiedMedication',
          'copiedMorbidities',
          'copiedComorbidities',
        ]
        COPIED_FLAGS.forEach(k => delete data[k])
        // Drop the UI-only auto/manual pack-years flag (approach B: not persisted).
        this.stripPackYearsMode(data)
        // Soft-required completeness: flag/warn about empty required fields, but
        // never block the save. A visit form is only "finished" once its required
        // fields are filled (otherwise it stays "inProgress"). Forms without
        // defined required fields keep the original per-chapter behaviour.
        const missing = this.missingRequired(data)
        this.requiredWarning = missing
        this.showRequiredErrors = missing.length > 0
        let status
        if (this.requiredFields.length) {
          status = missing.length ? 'inProgress' : 'finished'
        } else {
          status = this.chaptersList.length ? 'inProgress' : 'finished'
        }
        const updated = await rest.updateForm(this.patientId, this.formKey, {
          data,
          status,
        })
        // Keep local form data in sync so chapter switches don't lose unsaved merges.
        this.form = (updated && updated[0]) || { ...this.form, data, status }
        // originalValues tracks the (array-shaped) form model for change detection.
        this.originalValues = JSON.parse(JSON.stringify(this.values))
        // Clear the review flags from the live model so the banner/markers
        // disappear immediately (not just on next load).
        COPIED_FLAGS.forEach(k => this.$delete(this.values, k))
        this.saved = true
        this.markFields()
        // Advance to the next chapter so the user can keep filling the form.
        // The merged data is already persisted above, so nothing is lost.
        if (this.hasNextChapter) {
          this.$router.push({
            name: 'Visit',
            params: {
              projectSlug: this.projectSlug,
              patientId: this.patientId,
              visitNumber: this.visitNumber,
              chapter: String(this.activeIndex + 1),
            },
          })
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      } catch (e) {
        this.error = e.message || 'Save failed.'
      } finally {
        this.saving = false
      }
    },
    getChanges(original, current) {
      const changes = []
      const allKeys = new Set([
        ...Object.keys(original),
        ...Object.keys(current),
      ])
      const ignoredKeys = new Set([
        'patientId',
        'projectSlug',
        'updatedAt',
        'copiedAnthropometrics',
        'copiedSmoking',
        'copiedMedication',
        'copiedMorbidities',
        'copiedComorbidities',
      ])

      allKeys.forEach(k => {
        if (ignoredKeys.has(k)) return

        const oldVal = original[k]
        const newVal = current[k]

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          let label = k
          if (this.def) {
            const schemas = []
            if (this.def.chapters) {
              this.def.chapters.forEach(ch => {
                if (ch.schema) schemas.push(ch.schema)
              })
            }
            if (this.def.schema) {
              schemas.push(this.def.schema)
            }
            if (this.schema) {
              schemas.push(this.schema)
            }
            if (!this.def.chapters && !this.def.schema) {
              schemas.push(this.def)
            }

            const findLabel = fields => {
              if (!fields || !Array.isArray(fields)) return null
              for (const field of fields) {
                if (field.name === k && field.label) {
                  return field.label
                }
                if (field.children) {
                  const subLabel = findLabel(field.children)
                  if (subLabel) return subLabel
                }
              }
              return null
            }

            for (const s of schemas) {
              const foundLabel = findLabel(s)
              if (foundLabel) {
                label = foundLabel
                break
              }
            }
          }

          changes.push({
            key: k,
            label,
            oldValue: oldVal,
            newValue: newVal,
          })
        }
      })
      return changes
    },
    async submitCorrection() {
      this.showConfirmModal = false
      this.error = null
      this.saved = false
      this.saving = true
      try {
        const existing = (this.form && this.form.data) || {}
        const restored = toStoreValues(this.formFields, this.values, existing)
        const data = {
          ...existing,
          ...restored,
          patientId: this.patientId,
          projectSlug: this.projectSlug,
          updatedAt: new Date().toISOString(),
        }
        // Drop the UI-only auto/manual pack-years flag (approach B: not persisted).
        this.stripPackYearsMode(data)

        const status =
          (this.form && this.form.status) ||
          (this.chaptersList.length ? 'inProgress' : 'finished')

        const updated = await rest.updateForm(this.patientId, this.formKey, {
          data,
          status,
        })

        this.form = (updated && updated[0]) || { ...this.form, data, status }
        // Re-hydrate into the form (array) shape so inputs stay populated.
        this.values = toFormValues(
          this.formFields,
          (this.form && this.form.data) || {}
        )
        this.armPackYearsModes(this.values)
        this.originalValues = JSON.parse(JSON.stringify(this.values))
        this.saved = true
        this.markFields()
      } catch (e) {
        this.error = e.message || 'Correction failed.'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
